import { useState } from 'react';
import { EnvelopeIcon } from '@heroicons/react/24/solid';
import { Header, Footer } from '@ui';
import { getSupabaseClient } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const supabase = getSupabaseClient();
      const redirectUrl = `${window.location.origin}/auth/reset`;

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        setError(error.message);
        setLoading(false);
      } else {
        setSuccess(true);
        setLoading(false);
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const inputClasses =
    'w-full border border-[#2C2731] rounded-xl px-4 py-3 text-sm bg-[#FEF6D6] placeholder:text-[#2C2731]/50 focus:outline-none focus:ring-2 focus:ring-[#2C2731] focus:border-[#2C2731] transition-colors';

  return (
    <div className="min-h-screen flex flex-col bg-[#FFFCEF] text-[#2C2731]">
      <Header variant="login" />

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="max-w-md w-full text-center border border-[#2C2731] rounded-2xl bg-[#FFFCEF] px-8 py-10 shadow-sm">
          {/* Icon */}
          <div className="h-14 w-14 mx-auto flex items-center justify-center rounded-full bg-[#E8DFBD] text-[#2C2731] mb-8">
            <EnvelopeIcon className="h-6 w-6 text-[#2C2731]" />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-medium text-[#2C2731] mb-2 font-recoleta">
            Reset Your Password
          </h1>
          <p className="text-[#2C2731]/80 mb-8">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          {success ? (
            <div className="text-left">
              <div className="p-4 bg-[#E8DFBD] border border-[#2C2731] rounded-xl text-[#2C2731] mb-6">
                <div className="flex">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="h-5 w-5 text-[#2C2731]" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-[#2C2731]">Check your email</h3>
                    <p className="text-sm mt-2">
                      We've sent a password reset link to <strong>{email}</strong>.
                      Please check your inbox and click the link to reset your password.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/login')}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#304031] text-sm font-semibold transition bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90"
              >
                Back to Login
              </button>
            </div>
          ) : (
            <form className="text-left space-y-4" onSubmit={handleSubmit}>
              {error && (
                <div className="p-4 bg-[#2C2731] border border-[#FEF6D6] rounded-xl text-[#FEF6D6]">
                  <div className="flex">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="h-5 w-5 text-[#FEF6D6]" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[#2C2731] mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClasses}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#304031] text-sm font-semibold transition ${
                  loading
                    ? 'bg-[#304031]/70 text-[#fef6d6] cursor-not-allowed'
                    : 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin h-5 w-5 border-2 border-[#FEF6D6] border-t-transparent rounded-full"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <EnvelopeIcon className="h-5 w-5 text-[#fef6d6]" />
                    Send Reset Link
                  </>
                )}
              </button>

              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-[#2C2731]/70 hover:text-[#2C2731] transition-colors"
                >
                  Back to Login
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
